import React, { useState, useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import { TypographyVariantProps } from '../../atoms/Typography';
import { ClientContractBrandPanelProps } from './ClientContractBrandPanel.props';
import { ContentWrapperBox, HeadingWrapper, SelectBox, SelectionWrapper } from './ClientContractBrandPanel.styles';
import { SelectMenu } from '../../atoms/SelectMenu';
import { LocationNodeData, NodeType } from '../../../@types';
import { LiveSearchBox, LiveSearchListItem } from '../../atoms/LiveSearchBox';
import { LocationSearchItem } from '../LocationSearchItem';
import { getHasCodeForNodeSearch } from '../../../utils/services';
import { useSearchNodeByParentId } from '../../../queries/live-search-query';

export const ClientContractBrandPanel: React.FC<ClientContractBrandPanelProps> = ({
  clients,
  contracts,
  brands,
  clientId = 0,
  contractId = 0,
  brandId = 0,
  disableClient,
  disableContract,
  disableBrand,
  isNew,
  onChangeNodeType,
  locationSearchEnabled,
  locationData,
  onParentSelect,
  locationSearchParentNodeId,
  parentNodeId,
  error,
  pageType
}) => {
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedContract, setSelectedContract] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');

  const searchNodeByParentId = useSearchNodeByParentId();
  const liveSearchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (clients && clients.length === 1) {
      if (selectedClient === '0' || selectedClient === '') {
        setSelectedClient(clients[0].value);
        onChangeNodeType(NodeType.Contract, clients[0].value);
      } else {
        onChangeNodeType(NodeType.Contract, selectedClient);
      }
    }
  }, [clients]);

  useEffect(() => {
    if (contracts && contracts.length === 1) {
      if (selectedContract === '0' || selectedContract === '') {
        setSelectedContract(contracts[0].value);
        onChangeNodeType(NodeType.Brand, contracts[0].value);
      } else {
        onChangeNodeType(NodeType.Brand, selectedContract);
      }
    }
  }, [contracts]);

  useEffect(() => {
    // services related only implementation - handle scenario of including none option in contracts
    if (pageType === 'add-service' && contracts && contracts.length === 2 && contracts[0].value === '0') {
      setSelectedContract(contracts[1].value);
      onChangeNodeType(NodeType.Brand, contracts[1].value);
    }
  }, [pageType, contracts]);

  useEffect(() => {
    if (brands && brands.length === 1) {
      if (selectedBrand === '0' || selectedBrand === '') {
        setSelectedBrand(brands[0].value);
        onParentSelect && onParentSelect(parseInt(brands[0].value), brands.find((brand) => brand.value === brands[0].value)?.label || '', NodeType.Brand);
      } else {
        setSelectedBrand(selectedBrand);

        // To avoid resetting parent to Brand when a location is selected in the location live search
        if (locationData?.isFromLiveSearch) return;

        onParentSelect && onParentSelect(parseInt(selectedBrand), brands.find((brand) => brand.value === selectedBrand)?.label || '', NodeType.Brand);
      }
    }
  }, [brands]);

  useEffect(() => {
    // services related only implementation - handle scenario of including none option in brands
    if (pageType === 'add-service' && brands && brands.length === 2 && brands[0].value === '0' && selectedContract !== '0') {
      setSelectedBrand(brands[1].value);
    }
  }, [pageType, brands]);

  useEffect(() => {
    clientId > 0 && setSelectedClient(clientId.toString());
    contractId > 0 && setSelectedContract(contractId.toString());
    if (brandId > 0 && selectedBrand !== brandId?.toString()) {
      setSelectedBrand(brandId?.toString());

      // To avoid resetting parent to Brand when a location is selected in the location live search
      if (locationData?.isFromLiveSearch) return;

      onParentSelect && onParentSelect(brandId, brands.find((brand) => brand.value === brandId.toString())?.label || '', NodeType.Brand);
    }
    if (pageType === 'add-service' && clientId === 0) setSelectedClient('');
    if (contractId === 0) setSelectedContract('');
    if (brandId === 0) setSelectedBrand('');
  }, [clientId, contractId, brandId]);

  const getLocationSearchParentNodeId = () => {
    if (locationSearchParentNodeId) {
      return locationSearchParentNodeId;
    } else if ((selectedContract === '' || selectedContract === '0') && (selectedBrand === '' || selectedBrand === '0')) {
      return parseInt(selectedClient);
    } else if (selectedBrand === '' || selectedBrand === '0') {
      return parseInt(selectedContract);
    } else return parseInt(selectedBrand);
  };

  return (
    <ContentWrapperBox>
      {locationSearchEnabled && <HeadingWrapper variant={TypographyVariantProps.H6}>
        Select Parent
      </HeadingWrapper>}
      <SelectionWrapper>
        <Grid container spacing={1}>
          <Grid
            justifyContent="center"
            size={{
              xs: 12,
              md: locationSearchEnabled ? 3 : 4
            }}
          >
            <SelectBox>
              <SelectMenu
                id="clients-list"
                labelId="select-client"
                label="Select Client"
                items={clients}
                validate={error}
                selectedValue={selectedClient}
                disabled={!!parentNodeId || disableClient}
                onChange={(value) => {
                  setSelectedClient(value);
                  setSelectedContract('0');
                  setSelectedBrand('0');
                  onParentSelect && onParentSelect(0, '', '');
                  onChangeNodeType(NodeType.Contract, value);
                }}
              />
            </SelectBox>
          </Grid>
          <Grid
            justifyContent="center"
            size={{
              xs: 12,
              md: locationSearchEnabled ? 3 : 4
            }}
          >
            <SelectBox>
              <SelectMenu
                id="contracts-list"
                labelId="select-contract"
                label="Select Contract"
                items={contracts}
                selectedValue={selectedContract}
                disabled={!selectedClient || selectedClient === '0' || !!parentNodeId || disableContract}
                onChange={(value) => {
                  setSelectedContract(value);
                  setSelectedBrand('0');
                  onParentSelect && onParentSelect(0, '', NodeType.Contract);
                  onChangeNodeType(NodeType.Brand, value);
                  // services related only implementation - handle parent node assignment when contract is none
                  if (pageType === 'add-service' && value === '0') {
                    onChangeNodeType(NodeType.Brand, selectedClient);
                  }
                }}
              />
            </SelectBox>
          </Grid>
          <Grid
            justifyContent="center"
            size={{
              xs: 12,
              md: locationSearchEnabled ? 3 : 4
            }}
          >
            <SelectBox>
              <SelectMenu
                id="brands-list"
                labelId="select-brand"
                label="Select Brand"
                items={brands}
                selectedValue={selectedBrand}
                disabled={!selectedContract || selectedContract === '0' || !!parentNodeId || disableBrand}
                onChange={(value) => {
                  setSelectedBrand(value);
                  onParentSelect && onParentSelect(parseInt(value), brands.find((brand) => brand.value === value)?.label || '', NodeType.Brand);
                }}
              />
            </SelectBox>
          </Grid>
          {locationSearchEnabled &&
            <Grid
              justifyContent="center"
              size={{
                xs: 12,
                md: 3
              }}
            >
              <LiveSearchBox
                title="Location Search"
                timeOffset={400}
                value={locationData?.name || ''}
                inputRef={liveSearchRef}
                onClearValue={() => {
                  brandId !== 0 && onParentSelect && onParentSelect(parseInt(selectedBrand), brands.find((brand) => brand.value === selectedBrand)?.label || '', NodeType.Brand);
                  liveSearchRef.current?.focus();
                  liveSearchRef.current?.blur();
                }}
                renderItem={(props: any, option: any) => {
                  return (
                    <LiveSearchListItem {...props}>
                      <LocationSearchItem data={option} />
                    </LiveSearchListItem>
                  );
                }}
                onChange={(obj: LocationNodeData) => obj && onParentSelect && onParentSelect(obj?.id, obj?.name, obj?.nodeTypeCode, obj?.parentTree ? JSON.parse(obj.parentTree) : [], true)}
                onApiInvoke={async(name) => {
                  const hasCode = getHasCodeForNodeSearch(name);
                  const response = await searchNodeByParentId.mutateAsync({ selectedParentId: getLocationSearchParentNodeId(), name, hasCode: hasCode });
                  const responseWithoutCurrentNode = response.filter((result) => isNew || (locationData?.id && result.id !== locationData?.id));
                  return responseWithoutCurrentNode;
                }}
              />
            </Grid>
          }
        </Grid>
      </SelectionWrapper>
    </ContentWrapperBox>
  );
};